// shopify-buy.service.ts
import { Injectable } from '@angular/core';
import { BFF_URL, SHOPIFY_TOKENS } from '../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, forkJoin, map, switchMap } from 'rxjs';
import { SIM_ID } from '../app/app.config';
import { AuthService } from './auth.service';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class ShopifyBuyService {

  private domain = SHOPIFY_TOKENS.domain;
  private token = SHOPIFY_TOKENS.storefrontAccessToken;
  private shopifyUrl = 'v1/shopify'
  private headers = new HttpHeaders({
    'X-Shopify-Storefront-Access-Token': this.token,
    'Content-Type': 'application/json'
  });

  private url = `https://${this.domain}/api/2024-07/graphql.json`;
  constructor(private http: HttpClient, private authService: AuthService) {}

  getPlans() {
    const query = `
    {
      products(first: 50, query:"product_type:plan") {
        edges {
          node {
            id
            title
            productType
            variants(first: 40) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                  }
                  sellingPlanAllocations(first: 40) {
                    edges {
                      node {
                        sellingPlan {
                          id
                          name
                          description
                        }
                      }
                    }
                  }
                  metafields(identifiers: [
                    { namespace: "custom", key: "qc_included" },
                    { namespace: "custom", key: "data" },
                    { namespace: "custom", key: "featured" },
                    { namespace: "custom", key: "title" },
                    { namespace: "custom", key: "description" },
                    { namespace: "custom", key: "subscriptioncycles" },
                    { namespace: "custom", key: "archived" },
                    { namespace: "custom", key: "plan_id" }
                  ]) {
                    key
                    value
                  }
                }
              }
            }
          }
        }
      }
    }`;
  
    return this.http.post<any>(`${BFF_URL}/${this.shopifyUrl}`, { query }).pipe(
      map(res => {
        const products = res.data.products?.edges.map((p: any) => p.node) || [];
        
        return products.map((product: any) => ({
          id: product.id,
          title: product.title,
          variants: product.variants.edges.map((v: any) => {
            const variant = v.node;
  
            const plans = (variant.sellingPlanAllocations?.edges || []).map((edge: any) => {
              const plan = edge.node.sellingPlan;
              return {
                id: plan.id,
                name: plan.name,
                description: plan.description
              };
            });
  
            const metafields = (variant.metafields || []).reduce((acc: any, mf: any) => {
              if (!mf) return acc;
              const { key, value } = mf;
              acc[key] = value === null ? null : this.parseValue(value);
              return acc;
            }, {});
  
            const { sellingPlanAllocations, ...cleanVariantData } = variant;
  
            return { 
              ...cleanVariantData, 
              sellingPlans: plans, 
              metafields 
            };
          })
        }));
      })
    );
  } 
  
  getTelusFees() {
    const query = `
    {
      products(first: 50, query:"product_type:Fees") {
        edges {
          node {
            id
            title
            productType
            variants(first: 50) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                  }
                  sellingPlanAllocations(first: 10) {
                    edges {
                      node {
                        sellingPlan {
                          id
                          name
                          description
                        }
                      }
                    }
                  }
                  metafields(identifiers: [
                    { namespace: "custom", key: "subscription_cycles" },
                  ]) {
                    key
                    value
                  }
                }
              }
            }
          }
        }
      }
    }`;
  

    return this.http.post<any>(`${BFF_URL}/${this.shopifyUrl}`, { query }).pipe(
      map(res => {
        const products = res.data.products.edges.map((p: any) => p.node);
        return products.map((product: any) => ({
          id: product.id,
          title: product.title,
          variants: product.variants.edges.map((v: any) => {
            const variant = v.node;
            const plans = variant.sellingPlanAllocations?.edges?.map((edge: any) => edge.node.sellingPlan) || []
            return { ...variant, sellingPlans: plans};
          })
        }));
      })
    );
  }
  getVouchers() {
    const query = {
      query: `
      {
        products(first: 5, query: "product_type:voucher") {
          edges {
            node {
              id
              title
              productType
              variants(first: 10)
              {
                edges {
                  node {
                    id
                    title
                    price
                    {
                      amount
                    }
                    metafields(identifiers: [
                      { namespace: "custom", key: "voucherId" }
                    ]) {
                      key
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }`
    };
  
    return this.http.post<any>(`${BFF_URL}/${this.shopifyUrl}`,query).pipe(
      map(res => {
        // Flatten the response and map variants
        const products = res.data.products.edges.map((p: any) => p.node);
        return products.map(product => ({
          id: product.id,
          title: product.title,
          variants: product.variants.edges.map((v: any) => {
            const variant = v.node;
            const metafields = variant.metafields?.reduce((acc, { key, value }) => {
              acc[key] = this.parseValue(value);
              return acc;
            }, {}) || {};
            return { ...variant, metafields};
          })
        }));
      })
    );
  }
  
  
  getSIMs() {
    const query = {
      query: `
      {
        products(first: 5, query: "product_type:SIM") {
          edges {
            node {
              id
              title
              productType
              variants(first: 10)
              {
                edges {
                  node {
                    id
                    title
                    price
                    {
                      amount
                    }
                  }
                }
              }
            }
          }
        }
      }`
    };
  
    return this.http.post<any>(`${BFF_URL}/${this.shopifyUrl}`,query).pipe(
      map(res => {
        // Flatten the response and map variants
        const products = res.data.products.edges.map((p: any) => p.node);
        return products.map(product => ({
          id: product.id,
          title: product.title,
          variants: product.variants.edges.map((v: any) => v.node)
        }));
      })
    );
  }
  getTopupProduct() {
    const query = {
      query: `
      {
        products(first: 5, query: "product_type:balance") {
          edges {
            node {
              id
              title
              productType
              variants(first: 10)
              {
                edges {
                  node {
                    id
                    title
                    price
                    {
                      amount
                    }
                  }
                }
              }
            }
          }
        }
      }`
    };
  
    return this.http.post<any>(`${BFF_URL}/${this.shopifyUrl}`,query).pipe(
      map(res => {
        // Flatten the response and map variants
        const products = res.data.products.edges.map((p: any) => p.node);
        return products.map(product => ({
          id: product.id,
          title: product.title,
          variants: product.variants.edges.map((v: any) => v.node)
        }));
      })
    );
  }
 async addToCartAndRedirect(
  variantId: string,
  sellingPlanId: string,
  quantity: number = 1
) {

  // get only numeric ID if needed
  const cleanVariantId = variantId.includes('gid://')
    ? variantId.split('/').pop()
    : variantId;
  const cleanSellingId = sellingPlanId.includes('gid://')
    ? sellingPlanId.split('/').pop()
    : sellingPlanId;

  const url = `https://${SHOPIFY_TOKENS.domain}/cart/add?id=${cleanVariantId}&selling_plan=${cleanSellingId}&quantity=${quantity}&return_to=/cart`;

  window.location.href = url;
 }

 async checkoutWithMultipleItems(items: any[], attributes?: Record<string, string>) {
  const getNumericId = (id: string) => id.includes('gid://') ? id.split('/').pop() : id;

  const queryParams: string[] = [];

  // 1. Map each item explicitly as an array element in the query payload
  items.forEach((item, index) => {
    const cleanVariantId = getNumericId(item.variantId);
    
    queryParams.push(`items[${index}][id]=${cleanVariantId}`);
    queryParams.push(`items[${index}][quantity]=${item.quantity}`);
    
    // Explicitly pair the selling plan logic inside the exact same index block
    if (item.sellingPlanId) {
      const cleanSellingId = getNumericId(item.sellingPlanId);
      queryParams.push(`items[${index}][selling_plan]=${cleanSellingId}`);
    }
  });

  // 2. Safely process Cart Attributes
  if (attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      const encodedKey = encodeURIComponent(key);
      const encodedValue = encodeURIComponent(value);
      queryParams.push(`attributes[${encodedKey}]=${encodedValue}`);
    });
  }

  // 3. Ensure a direct route straight through to the Shopify checkout terminal
  queryParams.push('return_to=/checkout');

  // 4. Construct the URL. Clearing the cart first ensures old items don't mix in
  const baseUrl = `https://${SHOPIFY_TOKENS.domain}/cart/clear`;
  const finalUrl = `${baseUrl}?return_to=${encodeURIComponent('/cart/add?' + queryParams.join('&'))}`;
  sessionStorage.setItem('url', finalUrl );
  // 5. Fire the redirection breaking out of proxies or iframes
    window.location.href = finalUrl;
}

async createCheckoutWithVariant(
    variantId: string,
    planId: string,
    mdn:string,
    quantity: number = 1
  ): Promise<{ id: string; webUrl: string }> {
    const gid = variantId.startsWith('gid://')
      ? variantId
      : `gid://shopify/ProductVariant/${variantId}`;

    // Create new cart
    const createCartQuery = `
      mutation {
        cartCreate {
          cart { id checkoutUrl }
        }
      }
    `;
    const cartRes: any = await this.http
      .post(this.url, { query: createCartQuery }, { headers: this.headers })
      .toPromise();
    const cart = cartRes?.data?.cartCreate?.cart;
    if (!cart?.id) {
      throw new Error('Cart creation failed');
    }

    // Mirror old checkout attributes on cart
    const setCartAttributesQuery = `
      mutation {
        cartAttributesUpdate(
          cartId: "${cart.id}",
          note: "planId=${planId}, MDN=${mdn}",
          attributes: [
            { key: "planId", value: "${planId}" },
            { key: "MDN", value: ${mdn} }
          ]
        ) {
          cart { id }
        }
      }
    `;
    await this.http
      .post(this.url, { query: setCartAttributesQuery }, { headers: this.headers })
      .toPromise();

    // Add plan variant line with same attributes
    const addLineItemQuery = `
      mutation {
        cartLinesAdd(
          cartId: "${cart.id}",
          lines: [
            {
              merchandiseId: "${gid}",
              quantity: ${quantity},
              attributes: [
                { key: "planId", value: "${planId}" },
                { key: "MDN", value: "${mdn}" }
              ]
            }
          ]
        ) {
          cart { id checkoutUrl }
        }
      }
    `;
    const addLineRes: any = await this.http
      .post(this.url, { query: addLineItemQuery }, { headers: this.headers })
      .toPromise();
    const updatedCart = addLineRes?.data?.cartLinesAdd?.cart;
    if (!updatedCart?.checkoutUrl) {
      throw new Error('Unable to add line item to cart');
    }

    const payload = { checkoutId: updatedCart.id, planId };
    localStorage.setItem('pendingCheckout', JSON.stringify(payload));

    return { id: updatedCart.id, webUrl: updatedCart.checkoutUrl };
  }

  async createCheckoutWithVoucherVariant(
    variantId: string,
    mdn: string,
    quantity: number,
    swapPlanId?: string
  ): Promise<{ id: string; checkoutUrl: string }> {
  
    // Ensure variantId is a valid GID
    const gid = variantId.startsWith('gid://')
      ? variantId
      : `gid://shopify/ProductVariant/${variantId}`;
  
    // Create new cart
    const createCartQuery = `
    mutation {
      cartCreate {
        cart {
          id
          checkoutUrl
        }
      }
    }`;
  
    const cartRes: any = await this.http.post(this.url, { query: createCartQuery }, { headers: this.headers }).toPromise();
    const cart = cartRes.data.cartCreate.cart;

    // this is to set a cart attribute that can be used in shopify flow to identify this as a voucher checkout and apply any necessary logic.
    const cartAttributes = [{ key: 'has_voucher', value: 'true' }];
    if (swapPlanId) {
      cartAttributes.push({ key: 'swap_plan_id', value: swapPlanId });
    }
    const attributesQuery = JSON.stringify(cartAttributes).replace(/"([^"]+)":/g, '$1:');
    
    const setCartAttributesQuery = `
      mutation {
        cartAttributesUpdate(
          cartId: "${cart.id}",
          attributes: ${attributesQuery}
        ) {
          cart {
            id
          }
        }
      }`;
       await this.http.post(
        this.url,
        { query: setCartAttributesQuery },
        { headers: this.headers }
      ).toPromise();
    // Add voucher variant to cart with mdn and id
    const addLineItemQuery = `
    mutation {
      cartLinesAdd(
        cartId: "${cart.id}",
        lines: [
          {
            merchandiseId: "${gid}",
            quantity: ${quantity},
            attributes: [
              { key: "Phone Number", value: "${mdn}" },
              { key: "intent", value: "topup" }
            ]
          }
        ]
      ) {
        cart {
          id
          checkoutUrl
        }
      }
    }`;
  
    const addLineRes: any = await this.http.post(this.url, { query: addLineItemQuery }, { headers: this.headers }).toPromise();
    const updatedCart = addLineRes.data.cartLinesAdd.cart;
  
    return { id: updatedCart.id, checkoutUrl: updatedCart.checkoutUrl };
  }

  async createDynamicPayment(amount: number, mdn:string): Promise<string> {
    const query = `mutation draftOrderCreate($input: DraftOrderInput!) {
      draftOrderCreate(input: $input) {
        draftOrder {
          id
          invoiceUrl
        }
        userErrors {
          field
          message
        }
      }
    }
    `;
    
    const variables = {
      input: {
        lineItems: [{
          title: `Plan Payment amount for ${mdn}`,
          originalUnitPrice: amount, 
          quantity: 1,
          taxable: true,
          requiresShipping: false,
        }],
        customAttributes: [
          { "key": "has_voucher", "value": "true" },
          { "key": "mdn", "value":`${mdn}` },
          { "key": "intent", "value": "topup" }
        ],
      }
    };
  
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables })
    );
  
    const draftOrder = res?.data?.draftOrderCreate?.draftOrder;
    
    if (!draftOrder?.invoiceUrl) {
      throw new Error("Could not generate payment link");
    }
  
    return draftOrder.invoiceUrl;
  }

  async sendUpdatePaymentUrlEmail(id): Promise<string> {
    const query = `mutation customerPaymentMethodSendUpdateEmail($customerPaymentMethodId: ID!) {
      customerPaymentMethodSendUpdateEmail(customerPaymentMethodId: $customerPaymentMethodId) {
        customer {
          id
        }
        userErrors {
          field
          message
        }
      }
    }    
    `;
    const variables = {
      "customerPaymentMethodId": id
    };

  
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
    );
    const data = res?.url;
    
   
    return data;
  }
  async updateContractPayment(contractId, paymentId): Promise<any> {
    const updateMutation = `
    mutation subscriptionContractUpdate($id: ID!, $input: SubscriptionContractUpdateInput!) {
      subscriptionContractUpdate(contractId: $id, input: $input) {
        draft { id }
        userErrors { message }
      }
    }`;
    const variables = {
      id: contractId, 
      input: { paymentMethodId: paymentId } 
    };

    const commitMutation = `
    mutation subscriptionDraftCommit($draftId: ID!) {
      subscriptionDraftCommit(draftId: $draftId) {
        contract { id status }
        userErrors { message }
      }
    }`;

    this.http.post(`${BFF_URL}/v1/shopify/admin`, { query: updateMutation, variables}).pipe(
      switchMap((res:any) => {
        const draftId = res.data.subscriptionContractUpdate.draft?.id;
        const errors = res.data.subscriptionContractUpdate.userErrors;
  
        if (errors.length > 0) throw new Error(errors[0].message);
        return  this.http.post(`${BFF_URL}/v1/shopify/admin`, { query: commitMutation, variables: {draftId}})
      })
    )
    
   

  }
//Create the Draft
async createDraftContract(contractId: string): Promise<any> {
  const query = `
    mutation subscriptionContractUpdate($id: ID!) {
      subscriptionContractUpdate(contractId: $id) {
        draft { id }
        userErrors { field message }
      }
    }`;
  return firstValueFrom(this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables: { id: contractId } }));
}

async updateDraftContract(draftId: string, billingDate: string): Promise<any> {
  const query = `
    mutation subscriptionDraftUpdate($draftId: ID!, $input: SubscriptionDraftInput!) {
      subscriptionDraftUpdate(draftId: $draftId, input: $input) {
        draft { id }
        userErrors { field message }
      }
    }`;
  const variables = { draftId, input: { paymentMethodId: billingDate } };
  return firstValueFrom(this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables }));
}


//Commit the Draft
async commitDraftContract(draftId: string): Promise<any> {
  const query = `
    mutation subscriptionDraftCommit($draftId: ID!) {
      subscriptionDraftCommit(draftId: $draftId) {
        contract { id status }
        userErrors { field message }
      }
    }`;
  return firstValueFrom(this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables: { draftId } }));
}

async updateDraftLine(draftId: string, lineId: string, variantId: string, quantity: number, price): Promise<any> {
  const query = `
    mutation subscriptionDraftLineUpdate($draftId: ID!, $lineId: ID!, $input: SubscriptionLineUpdateInput!) {
      subscriptionDraftLineUpdate(draftId: $draftId, lineId: $lineId, input: $input) {
        draft { id }
        userErrors { field message }
      }
    }`;
    
  const variables = { 
    draftId, 
    lineId, 
    input: { 
      productVariantId: variantId, 
      quantity: quantity ,
      currentPrice: price
    } 
  };

  return firstValueFrom(this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables }));
}


  async disableContract(id): Promise<any> {
    const query = `mutation subscriptionContractPause($subscriptionContractId: ID!) {
      subscriptionContractPause(subscriptionContractId: $subscriptionContractId) {
        contract {
          id
          status
        }
        userErrors {
          field
          message
        }
      }
    }
    `;
    const variables = {
      "subscriptionContractId": id
    };

  
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
    );
    const result = res;
    
    return result;
  }

  async enableContract(id): Promise<any> {
    const query = `mutation subscriptionContractActivate($subscriptionContractId: ID!) {
      subscriptionContractActivate(subscriptionContractId: $subscriptionContractId) {
        contract {
          id
          status
        }
        userErrors {
          field
          message
        }
      }
    }
    `;
    const variables = {
      "subscriptionContractId": id
    };

  
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
    );
    const result = res;
    
    return result;
  }
  
  async createWebhook(): Promise<any> {
    const query = `
    mutation webhookSubscriptionCreate(
      $topic: WebhookSubscriptionTopic!,
      $callbackUrl: URL!
    ) {
      webhookSubscriptionCreate(
        topic: $topic,
        webhookSubscription: {
          callbackUrl: $callbackUrl,
          format: JSON
        }
      ) {
        webhookSubscription {
          id
          topic
          apiVersion {
            handle
          }
        }
        userErrors {
          field
          message
        }
      }
    }`;
    return firstValueFrom(this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, "variables": {
      "topic": "SUBSCRIPTION_CONTRACTS_UPDATE",
      "callbackUrl": "https://g2g-ca-bff-service-2ut6xndtmq-uc.a.run.app/v1/shopify/webhook/autopay-updated"
    } }));
  }
 
  async createCheckoutNewCart(
    variantId: string,
    {imei, billingProvince}: {imei?: string, billingProvince?:string},
    quantity: number = 1, telusVariantId?: string
  ): Promise<{ id: string; checkoutUrl: string }> {
  
    const gid = variantId.startsWith('gid://')
      ? variantId
      : `gid://shopify/ProductVariant/${variantId}`;
  
    const REQUIRED_ID = SIM_ID;
  
    const query = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
          }
        }
      }
    `;
    const lines: any[] = [];

    // Add Telus variant FIRST if it exists so it can be shown as last item in cart
    if (telusVariantId) {
      lines.push({
        merchandiseId: telusVariantId,
        quantity: 1,
        attributes: [{ key: "_fee_type", value: "tellus_processing" }]
      });
    }
  
    lines.push(
      {
        merchandiseId: REQUIRED_ID,
        quantity: 1,
        attributes: [{ key: "_required", value: "true" }]
      },
      {
        merchandiseId: gid,
        quantity,
      }
    );
    const variables: any = {
      input: {
        attributes: [{ key: "intent", value: "new" },{ key: "imei", value: imei || "N/A" }, { key: "billingProvince", value: billingProvince || "N/A" }],
        lines: lines
      }
    };

    const res: any = await firstValueFrom(
      this.http.post(this.url, { query, variables }, { headers: this.headers })
    );
  
    const cart = res?.data?.cartCreate?.cart;
    const token: any = await this.authService.getShopifyToken();
    if (!!token) {
      // Attach customerAccessToken if exists
      const setBuyerIdentityQuery = `
      mutation {
        cartBuyerIdentityUpdate(
          cartId: "${cart.id}",
          buyerIdentity: {
            customerAccessToken: "${token.shopifyToken}"
          }
        ) {
          cart {
            id
          }
        }
      }`;

      await this.http.post(
        this.url,
        { query: setBuyerIdentityQuery },
        { headers: this.headers }
      ).toPromise();

    }
    if (!cart?.checkoutUrl) throw new Error("Cart creation failed");
    
    return {
      id: cart.id,
      checkoutUrl: cart.checkoutUrl
    };
  }

  async createCheckoutWithRefillPlan(
    variantId: string,
    mdn: string,
    quantity: number = 1,
    sellingPlanId?: string,
    intent?: string,
    newBundleId?: string,
    telusVariant?: any
  ): Promise<{ id: string; checkoutUrl: string }> {
  
    const gid = variantId.startsWith('gid://')
      ? variantId
      : `gid://shopify/ProductVariant/${variantId}`;
  
    const createCartQuery = `
      mutation CreateCart {
        cartCreate {
          cart { id checkoutUrl }
        }
      }
    `;
    const cartRes: any = await this.http.post(this.url, { query: createCartQuery }, { headers: this.headers }).toPromise();
    const cart = cartRes.data.cartCreate.cart;
  
    const token: any = await this.authService.getShopifyToken();
    if (token?.shopifyToken) {
      const setBuyerIdentityQuery = `
        mutation SetBuyerIdentity($cartId: ID!, $customerAccessToken: String!) {
          cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: { customerAccessToken: $customerAccessToken }) {
            cart { id }
          }
        }
      `;
      await this.http.post(this.url, {
        query: setBuyerIdentityQuery,
        variables: { cartId: cart.id, customerAccessToken: token.shopifyToken }
      }, { headers: this.headers }).toPromise();
    }
  
    const cartAttributes = [
      { key: 'intent', value: intent || 'refill' },
      { key: 'mdn', value: mdn }
    ];
    if (newBundleId) {
      cartAttributes.push({ key: 'new_bundle_Id', value: newBundleId });
    }
  
    const setCartAttributesQuery = `
      mutation SetCartAttributes($cartId: ID!, $attributes: [AttributeInput!]!) {
        cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
          cart { id }
        }
      }
    `;
  
    await this.http.post(this.url, {
      query: setCartAttributesQuery,
      variables: {
        cartId: cart.id,
        attributes: cartAttributes
      }
    }, { headers: this.headers }).toPromise();
  
    const setCartNoteQuery = `
      mutation SetCartNote($cartId: ID!, $note: String!) {
        cartNoteUpdate(cartId: $cartId, note: $note) {
          cart { id }
        }
      }
    `;
  
    await this.http.post(this.url, {
      query: setCartNoteQuery,
      variables: {
        cartId: cart.id,
        note: `MDN: ${mdn}`
      }
    }, { headers: this.headers }).toPromise();
  
    const lineAttributes = [
      { key: 'mdn', value: mdn },
      { key: 'intent', value: intent || 'refill' }
    ];
    if (newBundleId) {
      lineAttributes.push({ key: 'new_bundle_Id', value: newBundleId });
    }
  
    const lines: any[] = [];
  //TODO: clean up code after testing
    if (telusVariant) {
      const cleanTelusGid = telusVariant.id.startsWith('gid://') ? telusVariant.id : `gid://shopify/ProductVariant/${telusVariant.id}`;
      lines.push({
        merchandiseId: cleanTelusGid,
        quantity: 1,
        sellingPlanId: sellingPlanId ? telusVariant.sellingPlans[0].id : null,
        attributes: [{ key: "_fee_type", value: "tellus_processing" }]
      });
    } 
    if (sellingPlanId) {
        const cleanSellingPlanGid = sellingPlanId.startsWith('gid://') 
        ? sellingPlanId 
        : `gid://shopify/SellingPlan/${sellingPlanId}`;

        const storefrontSellingPlanId = btoa(cleanSellingPlanGid);
        lines.push({
        merchandiseId: gid,
        quantity: quantity,
        sellingPlanId: storefrontSellingPlanId,
        attributes: lineAttributes
        });
    } else {
      lines.push({
        merchandiseId: gid,
        quantity: quantity,
        attributes: lineAttributes
        });
    }

    const addLineItemQuery = `
      mutation AddCartLines($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart {
            id
            checkoutUrl
          }
          userErrors {
            field
            message
          }
        }
      }
    `;
    const addLineRes: any = await this.http.post(this.url, {
      query: addLineItemQuery,
      variables: {
        cartId: cart.id,
        lines: lines
      }
    }, { headers: this.headers }).toPromise();
  
    const addLineData = addLineRes.data.cartLinesAdd;
    
    if (addLineData.userErrors && addLineData.userErrors.length > 0) {
      throw new Error(`Shopify Cart Error: ${addLineData.userErrors[0].message}`);
    }
  
    const updatedCart = addLineData.cart;
  
    return {
      id: updatedCart.id,
      checkoutUrl: updatedCart.checkoutUrl
    };
  }

  public parseValue(value: string): any {
    if (value === 'true') return true;
    if (value === 'false') return false;
  
    if (!isNaN(Number(value))) return Number(value);
  
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  async getUserPayments(customerId): Promise<any> {
    const query = `query getCustomerPaymentMethods($id: ID!) {
      customer(id: $id) {
        id
        email
        paymentMethods(first: 10) {
          edges {
            node {
              id
              instrument {
                ... on CustomerCreditCard {
                  brand
                  lastDigits
                  expiryMonth
                  expiryYear
                  name
                }
                ... on CustomerPaypalBillingAgreement {
                  paypalAccountEmail
                }
              }
              revokedAt
              revokedReason
            }
          }
        }
      }
    }
    `;
    const variables = {
      "id": customerId
    };

  
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
    );
    const data = res?.url;
    
   
    return data;
  }
  async getsubs(customerId): Promise<any> {
    const query = `query getCustomerSubscriptions($customerId: ID!, $first: Int!) {
      customer(id: $customerId) {
        id
        subscriptionContracts(first: $first) {
          edges {
            node {
              id
              # ... existing contract fields
              lines(first: 10) {
                edges {
                  node {
                    id
                    variantId
                    # Fetching the actual Variant object
                    variant {
                      id
                      title
                      # Accessing Variant Metafields (Custom Attributes)
                      metafields(first: 10) {
                        edges {
                          node {
                            key
                            value
                            namespace
                          }
                        }
                      }
                      # Standard Variant fields if needed
                      sku
                      barcode
                        name
                        value
                      }
                    }
                    customAttributes {
                      key
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    
    `;
    const variables = {
      "customerId": customerId
    };

  
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
    );
    const data = res?.url;
    
   
    return data;
  }
  async createSubscriptions() {
    const query = `mutation sellingPlanGroupAddProductVariants($id: ID!, $productVariantIds: [ID!]!) {
      sellingPlanGroupAddProductVariants(id: $id, productVariantIds: $productVariantIds) {
        sellingPlanGroup {
          id
          name
        }
        userErrors {
          field
          message
        }
      }
    }
    `;
    const variables = {
      "id": "gid://shopify/SellingPlanGroup/1708261532",
      "productVariantIds": [
        "gid://shopify/ProductVariant/46759612776604",
        "gid://shopify/ProductVariant/46759612809372",
        "gid://shopify/ProductVariant/46759612842140",
        "gid://shopify/ProductVariant/46759612874908",
        "gid://shopify/ProductVariant/46759612907676",
        "gid://shopify/ProductVariant/46759612940444",
        "gid://shopify/ProductVariant/46759612973212",
        "gid://shopify/ProductVariant/46759613005980",
        "gid://shopify/ProductVariant/46759613038748",
        "gid://shopify/ProductVariant/46759613071516",
        "gid://shopify/ProductVariant/46759613104284",
        "gid://shopify/ProductVariant/46759613137052",
        "gid://shopify/ProductVariant/46759613169820",
      ]
    }
    

    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
    );
    const data = res;
   
    return data;
    
  }
  async retryBillingAttempt(contract): Promise<any> {
    const query = `mutation CreateBillingAttempt($id: ID!, $subscriptionBillingAttemptInput: SubscriptionBillingAttemptInput!) {
      subscriptionBillingAttemptCreate(subscriptionContractId: $id, subscriptionBillingAttemptInput: $subscriptionBillingAttemptInput) {
        subscriptionBillingAttempt {
          id
          ready
          errorCode
          errorMessage
          order {
            id
            name
          }
        }
        userErrors {
          field
          message
        }
      }
    }`;
    const variables = {
      "id": contract.contractID,
      "paymentProcessingPolicy": "FAIL_UNLESS_VALID_PAYMENT_METHOD",
      "subscriptionBillingAttemptInput": {
        "idempotencyKey": uuidv4(),
        "originTime": new Date().toISOString(),
      }
    };

    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
    );
    const data = res;
   
    return data;
  }
  async createSellingGroupForProduct(): Promise<any> {
    const query = `mutation CreateAnnualFeesSubscriptionGroup {
      sellingPlanGroupCreate(
        input: {
          name: "Annual e-911 Fees"
          merchantCode: "annual-fees"
          options: ["Delivery Frequency"]
          sellingPlansToCreate: [
            {
              name: "Annual Fees"
              description: "Billed once every year."
              category: SUBSCRIPTION 
              options: "Every 1 Year"
              billingPolicy: {
                recurring: {
                  interval: YEAR
                  intervalCount: 1
                }
              }
              deliveryPolicy: {
                recurring: {
                  interval: YEAR
                  intervalCount: 1
                }
              }
              pricingPolicies: [
              ]
            }
          ]
        }
        resources: {
          productIds: ["gid://shopify/Product/9004318589084"]
        }
      ) {
        sellingPlanGroup {
          id
          name
        }
        userErrors {
          field
          message
        }
      }
    }    
    `;

  
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query})
    );
    const result = res;
    
    return result;
  }
  async getAllGroups(): Promise<any> {
    const query = `query GetAllGroupsWithTheirVariants {
      sellingPlanGroups(first: 20) {
        edges {
          node {
            id
            name
            merchantCode
            
            # Pulls the specific variants linked to this group
            productVariants(first: 50) {
              edges {
                node {
                  id
                  title
                  sku
                  # Shows the parent product details
                  product {
                    id
                    title
                  }
                }
              }
            }
          }
        }
      }
    }
    
    `;
  
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query})
    );
    const result = res;
    
    return result;
  }
  async addVariantToGroup(): Promise<any> {
    const query = `mutation AddVariantToGroup {
      sellingPlanGroupAddProductVariants(
        id: "gid://shopify/SellingPlanGroup/2415591609"
    
        productVariantIds: [
          "gid://shopify/ProductVariant/47780025172153",
          "gid://shopify/ProductVariant/47780235018425"
        ]
      ) {
        sellingPlanGroup {
          id
          name
          # Verify that the variant is now attached
          productVariants(first: 5) {
            edges {
              node {
                id
                title
              }
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }    
    `;
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query})
    );
    const result = res;
    
    return result;
  }
  async removeSubscriptionGroup(): Promise<any> {
    const query = `mutation deleteSubscriptionGroup($id: ID!) {
      sellingPlanGroupDelete(id: $id) {
        deletedSellingPlanGroupId
        userErrors {
          field
          message
        }
      }
    }`;
    
    const variables = {
        "id": "gid://shopify/SellingPlanGroup/1750171804",
    };

  
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
    );
    const result = res;
    
    return result;
  }
  async removeVariantFromGroup(): Promise<any> {
    const query = `mutation removeVariantsFromSubscriptionGroup($id: ID!, $productVariantIds: [ID!]!) {
      sellingPlanGroupRemoveProductVariants(id: $id, productVariantIds: $productVariantIds) {
        removedProductVariantIds
        userErrors {
          field
          message
        }
      }
    }    
    `;
    const variables = {
      "id": "gid://shopify/SellingPlanGroup/1750106268",
      "productVariantIds": [
        "gid://shopify/ProductVariant/46759612776604"
      ]
    }
    ;
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
    );
    const result = res;
    
    return result;
  }
  async removeProductFromGroup(): Promise<any> {
    const query = `mutation removeProductFromGroup($id: ID!, $productIds: [ID!]!) {
      sellingPlanGroupRemoveProducts(id: $id, productIds: $productIds) {
        removedProductIds
        userErrors {
          field
          message
        }
      }
    }
    `;
    const variables = {
        "id": "gid://shopify/SellingPlanGroup/1708261532",
        "productIds": ["gid://shopify/Product/9004318589084"]
      
    }
    ;
    const res: any = await firstValueFrom(
      this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
    );
    const result = res;
    
    return result;
  }
  async cancelSubscription(id): Promise<any> {
      const query = `mutation CancelSubscriptionContract($subscriptionContractId: ID!) {
        subscriptionContractCancel(subscriptionContractId: $subscriptionContractId) {
          contract {
            id
            status
          }
          userErrors {
            field
            message
          }
        }
      }
      
      `;
      const variables = {
        "subscriptionContractId":id
        
      }
      ;
      const res: any = await firstValueFrom(
        this.http.post(`${BFF_URL}/v1/shopify/admin`, { query, variables})
      );
      const result = res;
      
      return result;
  }
}